import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert, 
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { 
  Search, 
  FilterList, 
  Refresh 
} from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { 
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable 
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { listControlTables } from '../../../../graphql/queries';
import StatusChip from '../common/StatusChip';
import TaskFilterDialog from './TaskFilterDialog';
import TaskDetailDialog from './TaskDetailDialog';

const TaskMonitor = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    dataSource: '',
    dataCategory: '',
    search: ''
  });
  
  // Load tasks from API
  const fetchTasks = async () => {
    setLoading(true);
    try {
      // Build filter parameters based on current filters
      let filter = {};
      
      if (filters.status) {
        filter.status = { eq: filters.status };
      }
      
      if (filters.dataSource) {
        filter.dataSource = { eq: filters.dataSource };
      }
      
      if (filters.dataCategory) {
        filter.dataCategory = { eq: filters.dataCategory };
      }
      
      if (filters.search) {
        // Simple contains search across multiple fields
        filter.or = [
          { taskId: { contains: filters.search } },
          { sourceIdentifier: { contains: filters.search } },
          { executionId: { contains: filters.search } }
        ];
      }
      
      const variables = {
        limit: 100,
        sort: { field: 'lastUpdated', direction: 'desc' }
      };
      
      if (Object.keys(filter).length > 0) {
        variables.filter = filter;
      }
      console.log('variables', variables)
      const response = await API.graphql(graphqlOperation(listControlTables, variables));
      console.log('listControlTables response', response)
      
      setTasks(response.data.listControlTables.items);
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTasks();
    
    // Set up polling for ongoing tasks
    const interval = setInterval(() => {
      // Only refresh if we're displaying active tasks
      if (!filters.status || filters.status === 'PENDING' || filters.status === 'PROCESSING') {
        fetchTasks();
      }
    }, 30000); // Poll every 30 seconds
    
    return () => clearInterval(interval);
  }, [filters]);
  
  // Handle search input
  const handleSearchChange = (event) => {
    setFilters({
      ...filters,
      search: event.target.value
    });
  };
  
  // Apply filters from dialog
  const applyFilters = (newFilters) => {
    setFilters({
      ...filters,
      ...newFilters
    });
    setShowFilterDialog(false);
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      dataSource: '',
      dataCategory: '',
      search: ''
    });
    setShowFilterDialog(false);
  };
  
  // Handle task row click
  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };
  
  // Retry a failed task (to be implemented)
  const handleRetryTask = async (taskId) => {
    try {
      console.log(`Retry task with ID: ${taskId}`);
      // API call to retry task would go here
      // For now, just refresh the list
      fetchTasks();
      setSelectedTask(null);
    } catch (err) {
      console.error('Error retrying task:', err);
    }
  };
  
  // Table column definitions
  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor('taskId', {
      header: 'Task ID',
      cell: info => info.getValue(),
      size: 250
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: info => <StatusChip status={info.getValue()} />,
      size: 120
    }),
    columnHelper.accessor('dataSource', {
      header: 'Data Source',
      cell: info => info.getValue(),
      size: 150
    }),
    columnHelper.accessor('dataCategory', {
      header: 'Category',
      cell: info => info.getValue(),
      size: 120
    }),
    columnHelper.accessor('sourceIdentifier', {
      header: 'Source ID',
      cell: info => info.getValue(),
      size: 120
    }),
    columnHelper.accessor('startTime', {
      header: 'Start Time',
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'yyyy-MM-dd HH:mm:ss') : '-',
      size: 180
    }),
    columnHelper.accessor('endTime', {
      header: 'End Time',
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'yyyy-MM-dd HH:mm:ss') : '-',
      size: 180
    }),
    columnHelper.accessor('processingDuration', {
      header: 'Duration (s)',
      cell: info => info.getValue() ? Math.round(info.getValue() / 1000) : '-',
      size: 120
    }),
    columnHelper.accessor('recordsProcessed', {
      header: 'Records',
      cell: info => info.getValue() || '-',
      size: 100
    })
  ];
  
  // Setup table instance
  const table = useReactTable({
    data: tasks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          Task Control Table
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            placeholder="Search tasks..."
            variant="outlined"
            size="small"
            value={filters.search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilterDialog(true)}
            sx={{
              borderColor: '#757575',
              color: '#424242',
              '&:hover': {
                borderColor: '#424242',
                backgroundColor: 'rgba(66, 66, 66, 0.04)',
              },
              textTransform: 'none',
            }}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTasks}
            sx={{
              borderColor: '#757575',
              color: '#424242',
              '&:hover': {
                borderColor: '#424242',
                backgroundColor: 'rgba(66, 66, 66, 0.04)',
              },
              textTransform: 'none',
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {/* Task Table */}
      <Paper sx={{ mb: 3 }}>
        {loading && tasks.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{ 
                          padding: '12px', 
                          textAlign: 'left', 
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          width: header.getSize?.() || 'auto'
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted()] ?? ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr 
                    key={row.id} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleTaskClick(row.original)}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td 
                        key={cell.id}
                        style={{ 
                          padding: '12px', 
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          width: cell.column.getSize?.() || 'auto'
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                
                {table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} style={{ padding: '24px', textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No tasks found
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  {'<<'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  {'<'}
                </Button>
                <Typography variant="body2" sx={{ mx: 2 }}>
                  Page{' '}
                  <strong>
                    {table.getState().pagination.pageIndex + 1} of{' '}
                    {table.getPageCount()}
                  </strong>
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  {'>'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  {'>>'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
      
      {/* Filter Dialog */}
      <TaskFilterDialog 
        open={showFilterDialog}
        onClose={() => setShowFilterDialog(false)}
        currentFilters={filters}
        onApplyFilters={applyFilters}
        onClearFilters={clearFilters}
      />
      
      {/* Task Detail Dialog */}
      {selectedTask && (
        <TaskDetailDialog
          task={selectedTask}
          open={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onRetry={() => handleRetryTask(selectedTask.taskId)}
        />
      )}
    </Box>
  );
};

export default TaskMonitor;